import React from 'react';
import { ArrowRight, Trash2, Snowflake, CheckCircle2 } from 'lucide-react';
import { stats } from '../data/mock';

const HeroSection = ({ onGetEstimate }) => {
  return (
    <section className="relative min-h-screen pt-20 bg-[#0A0A0A]">
      {/* Main Split Hero */}
      <div className="relative min-h-[600px] md:h-[80vh] flex flex-col md:flex-row">
        {/* Left Side - Junk Removal (Summer/Warm) */}
        <div className="relative w-full md:w-1/2 min-h-[400px] md:min-h-full overflow-hidden group">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1570630591157-9cea0732d4f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGJhY2t5YXJkfGVufDB8fHx8MTc2OTA2MTcxNnww&ixlib=rb-4.1.0&q=85')`
            }}
          />
          {/* Warm Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600/90 via-orange-500/80 to-green-600/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
          
          {/* Content */}
          <div className="relative h-full flex flex-col justify-center items-center text-center px-6 py-12 z-10">
            {/* Icon Badge */}
            <div className="w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 border border-white/30">
              <Trash2 className="w-8 h-8 md:w-10 md:h-10 text-white" />
            </div>
            
            {/* Season Label */}
            <span className="text-white/80 text-xs md:text-sm font-semibold tracking-widest uppercase mb-2">
              Summer Services
            </span>
            
            {/* Title */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">
              Junk Removal
            </h2>
            
            {/* Description */}
            <p className="text-white/90 text-base md:text-lg max-w-md mb-4 leading-relaxed">
              Clear the clutter from your home, garage, or yard. We handle the heavy lifting.
            </p>
            
            {/* Features */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {['Same-Day', 'Eco-Friendly', 'Fair Pricing'].map((feature) => (
                <div key={feature} className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-300" />
                  <span className="text-white text-xs md:text-sm font-medium">{feature}</span>
                </div>
              ))}
            </div>
            
            {/* CTA Button */}
            <button
              onClick={onGetEstimate}
              className="group/btn bg-white text-orange-600 px-6 md:px-8 py-3 md:py-4 rounded-full font-bold text-base md:text-lg hover:bg-orange-100 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-orange-500/30 flex items-center gap-2"
            >
              Get a Junk Quote
              <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Right Side - Snow Removal (Winter/Cool) */}
        <div className="relative w-full md:w-1/2 min-h-[400px] md:min-h-full overflow-hidden group">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1708630283437-c47ec20376a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxjbGVhcmVkJTIwc25vdyUyMGRyaXZld2F5fGVufDB8fHx8MTc2OTA2MTcyMXww&ixlib=rb-4.1.0&q=85')`
            }}
          />
          {/* Cool Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-bl from-blue-600/90 via-blue-500/80 to-cyan-400/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
          
          {/* Content */}
          <div className="relative h-full flex flex-col justify-center items-center text-center px-6 py-12 z-10">
            {/* Icon Badge */}
            <div className="w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 border border-white/30">
              <Snowflake className="w-8 h-8 md:w-10 md:h-10 text-white" />
            </div>
            
            {/* Season Label */}
            <span className="text-white/80 text-xs md:text-sm font-semibold tracking-widest uppercase mb-2">
              Winter Services
            </span>
            
            {/* Title */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">
              Snow Clearing
            </h2>
            
            {/* Description */}
            <p className="text-white/90 text-base md:text-lg max-w-md mb-4 leading-relaxed">
              Keep your driveway and walkways safe and accessible all winter long.
            </p>
            
            {/* Features */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {['24/7 Emergency', 'Residential', 'Commercial'].map((feature) => (
                <div key={feature} className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-300" />
                  <span className="text-white text-xs md:text-sm font-medium">{feature}</span>
                </div>
              ))}
            </div>
            
            {/* CTA Button */}
            <button
              onClick={onGetEstimate}
              className="group/btn bg-white text-blue-600 px-6 md:px-8 py-3 md:py-4 rounded-full font-bold text-base md:text-lg hover:bg-blue-50 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-500/30 flex items-center gap-2"
            >
              Book Snow Clearing
              <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Center Divider with Logo - Desktop Only */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30 hidden md:block">
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-[#7BA428] blur-3xl opacity-30 scale-150" />
            {/* Main Badge */}
            <div className="relative bg-[#0A0A0A] border-4 border-[#7BA428] rounded-full p-5 shadow-2xl">
              <img 
                src="logo.png" 
                alt="K&A Multi-Services" 
                className="h-20 w-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section with Main Headline & Stats */}
      <div className="relative bg-[#0A0A0A] py-12 md:py-16">
        {/* Main Headline */}
        <div className="container mx-auto px-4 text-center mb-10">
          <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white leading-tight mb-4 md:mb-6">
            We Clear Your <span className="text-orange-500">Junk</span>,
            <br />
            We Clear Your <span className="text-blue-400">Snow</span>.
          </h1>
          <p className="text-gray-400 text-base md:text-lg lg:text-xl max-w-2xl mx-auto">
            Edmonton's trusted partner for year-round property maintenance. 
            <span className="text-white font-semibold"> Ready to work.</span>
          </p>
        </div>

        {/* Stats */}
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="bg-[#141414] border border-[#2A2A2A] rounded-2xl p-4 md:p-6 text-center hover:border-[#7BA428]/50 transition-all"
              >
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#7BA428] mb-1">
                  {stat.value}
                </div>
                <div className="text-gray-400 font-medium text-xs md:text-sm">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
